import { useState } from "react"
import { useParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiTenant, ApiError } from "../lib/api"

type EmployeeStatus = "active" | "on_leave" | "terminated"
type WorkerType = "employee" | "isp_contractor" | "casual"

interface Employee {
  id: string
  employeeNo: string
  fullName: string
  workerType: WorkerType
  status: EmployeeStatus
  email: string | null
  phone: string | null
  joinedAt: string
  terminatedAt: string | null
}

interface EmployeesResponse {
  employees: Employee[]
  nextCursor: string | null
}

const STATUS_PILL: Record<EmployeeStatus, string> = {
  active: "bg-emerald-100 text-emerald-700",
  on_leave: "bg-amber-100 text-amber-700",
  terminated: "bg-slate-200 text-slate-600",
}

export function EmployeesPage() {
  const { slug = "" } = useParams<{ slug: string }>()
  const qc = useQueryClient()
  const [q, setQ] = useState("")
  const [statusFilter, setStatusFilter] = useState<EmployeeStatus | "">("")
  const [showHire, setShowHire] = useState(false)
  const [terminateTarget, setTerminateTarget] = useState<Employee | null>(null)
  const [editTargetId, setEditTargetId] = useState<string | null>(null)

  const queryKey = ["employees", slug, q, statusFilter] as const

  const { data, isLoading, error } = useQuery({
    queryKey,
    enabled: !!slug,
    queryFn: () => {
      const params = new URLSearchParams()
      if (q.trim()) params.set("q", q.trim())
      if (statusFilter) params.set("status", statusFilter)
      const qs = params.toString()
      return apiTenant<EmployeesResponse>(slug, `/employees${qs ? `?${qs}` : ""}`)
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ["employees", slug] })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Employees</h1>
          <p className="text-xs text-slate-500 mt-1">Tenant <span className="font-mono">{slug}</span></p>
        </div>
        <button
          onClick={() => setShowHire(true)}
          className="px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded"
        >
          + Hire employee
        </button>
      </div>

      <div className="flex gap-2 items-center">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, employee #, email"
          className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm focus:border-brand-500 focus:outline-none"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as EmployeeStatus | "")}
          className="px-3 py-2 border border-slate-300 rounded text-sm bg-white"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="on_leave">On leave</option>
          <option value="terminated">Terminated</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded">
        {isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
        {error && <div className="p-6 text-sm text-red-600">{(error as Error).message}</div>}
        {data && data.employees.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-400">
            No employees yet. Click <strong className="text-slate-600">+ Hire employee</strong> to add the first one.
          </div>
        )}
        {data && data.employees.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <tr>
                <th className="text-left font-medium px-4 py-2">Employee #</th>
                <th className="text-left font-medium px-4 py-2">Name</th>
                <th className="text-left font-medium px-4 py-2">Worker type</th>
                <th className="text-left font-medium px-4 py-2">Joined</th>
                <th className="text-left font-medium px-4 py-2">Status</th>
                <th className="text-right font-medium px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.employees.map((emp) => (
                <tr key={emp.id} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-4 py-2 font-mono text-xs">{emp.employeeNo}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-900">{emp.fullName}</div>
                    {emp.email && <div className="text-xs text-slate-500">{emp.email}</div>}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{emp.workerType.replace(/_/g, " ")}</td>
                  <td className="px-4 py-2 text-slate-600">{emp.joinedAt.slice(0, 10)}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded ${STATUS_PILL[emp.status]}`}>
                      {emp.status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => setEditTargetId(emp.id)}
                      className="text-xs text-brand-600 hover:text-brand-800 mr-3"
                    >
                      Edit
                    </button>
                    {emp.status !== "terminated" && (
                      <button
                        onClick={() => setTerminateTarget(emp)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Terminate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showHire && (
        <HireEmployeeModal
          slug={slug}
          onClose={() => setShowHire(false)}
          onCreated={() => {
            invalidate()
            setShowHire(false)
          }}
        />
      )}

      {terminateTarget && (
        <TerminateEmployeeModal
          slug={slug}
          employee={terminateTarget}
          onClose={() => setTerminateTarget(null)}
          onTerminated={() => {
            invalidate()
            setTerminateTarget(null)
          }}
        />
      )}

      {editTargetId && (
        <EditEmployeeModal
          slug={slug}
          employeeId={editTargetId}
          onClose={() => setEditTargetId(null)}
          onSaved={() => {
            invalidate()
            setEditTargetId(null)
          }}
        />
      )}
    </div>
  )
}

function HireEmployeeModal(props: { slug: string; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    employeeNo: "",
    fullName: "",
    workerType: "employee" as WorkerType,
    email: "",
    phone: "",
    nic: "",
    epfNo: "",
    joinedAt: new Date().toISOString().slice(0, 10),
    // Starting contract fields — required for the employee to appear on
    // payroll runs. Without a contract the run driver silently skips them.
    position: "",
    baseSalary: "",
    payFrequency: "monthly" as "monthly" | "daily" | "piece_rate",
  })
  const [error, setError] = useState("")

  const mutation = useMutation({
    mutationFn: async () => {
      // Step 1: create the employee.
      const empRes = await apiTenant<{ employee: { id: string } }>(props.slug, "/employees", {
        method: "POST",
        body: JSON.stringify({
          employeeNo: form.employeeNo.trim(),
          fullName: form.fullName.trim(),
          workerType: form.workerType,
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          nic: form.nic.trim() || null,
          epfNo: form.epfNo.trim() || null,
          joinedAt: form.joinedAt,
        }),
      })
      // Step 2: create the starting contract. Failure here leaves the
      // employee orphaned without a contract — surfaced as an error so
      // the admin knows to fix from Edit.
      try {
        await apiTenant(props.slug, "/contracts", {
          method: "POST",
          body: JSON.stringify({
            employeeId: empRes.employee.id,
            effectiveFrom: form.joinedAt,
            contractType: form.workerType === "isp_contractor" ? "isp" : "permanent",
            position: form.position.trim(),
            baseSalary: Number(form.baseSalary),
            payFrequency: form.payFrequency,
          }),
        })
      } catch (err) {
        const msg = err instanceof Error ? err.message : "contract create failed"
        throw new Error(`Employee created but starting contract failed: ${msg}. Open Edit on the new row to add one.`)
      }
    },
    onSuccess: props.onCreated,
    onError: (err) => {
      if (err instanceof ApiError) {
        const details = (err.body as { details?: Array<{ path?: string[]; message: string }> } | null)?.details
        if (Array.isArray(details) && details[0]) {
          setError(`${details[0].path?.join(".")} — ${details[0].message}`)
        } else {
          setError(err.message)
        }
      } else {
        setError(err instanceof Error ? err.message : "Failed to create")
      }
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError("")
    if (!form.employeeNo.trim() || !form.fullName.trim() || !form.joinedAt) {
      setError("Employee #, full name, and joined date are required.")
      return
    }
    if (!form.position.trim() || !form.baseSalary) {
      setError("Position and base salary are required for the starting contract.")
      return
    }
    if (Number(form.baseSalary) <= 0) {
      setError("Base salary must be greater than zero.")
      return
    }
    mutation.mutate()
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl"
      >
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="font-semibold">Hire employee</div>
          <button type="button" onClick={props.onClose} className="text-slate-400 hover:text-slate-700">
            ×
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Employee # *">
              <input
                value={form.employeeNo}
                onChange={(e) => setForm({ ...form, employeeNo: e.target.value })}
                className="form-input"
                placeholder="E001"
              />
            </Field>
            <Field label="Worker type">
              <select
                value={form.workerType}
                onChange={(e) => setForm({ ...form, workerType: e.target.value as WorkerType })}
                className="form-input"
              >
                <option value="employee">Employee</option>
                <option value="isp_contractor">ISP contractor</option>
                <option value="casual">Casual</option>
              </select>
            </Field>
          </div>
          <Field label="Full name *">
            <input
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              className="form-input"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email">
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="form-input"
              />
            </Field>
            <Field label="Phone">
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="form-input"
              />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="NIC">
              <input
                value={form.nic}
                onChange={(e) => setForm({ ...form, nic: e.target.value })}
                className="form-input"
              />
            </Field>
            <Field label="EPF #">
              <input
                value={form.epfNo}
                onChange={(e) => setForm({ ...form, epfNo: e.target.value })}
                className="form-input"
              />
            </Field>
            <Field label="Joined *">
              <input
                type="date"
                value={form.joinedAt}
                onChange={(e) => setForm({ ...form, joinedAt: e.target.value })}
                className="form-input"
              />
            </Field>
          </div>

          <div className="text-xs font-medium text-slate-500 mt-4 mb-1">Starting contract</div>
          <Field label="Position *">
            <input
              value={form.position}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
              className="form-input"
              placeholder="e.g. Software Engineer"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Base salary (LKR) *">
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.baseSalary}
                onChange={(e) => setForm({ ...form, baseSalary: e.target.value })}
                className="form-input"
                placeholder="100000"
              />
            </Field>
            <Field label="Pay frequency">
              <select
                value={form.payFrequency}
                onChange={(e) => setForm({ ...form, payFrequency: e.target.value as typeof form.payFrequency })}
                className="form-input"
              >
                <option value="monthly">Monthly</option>
                <option value="daily">Daily</option>
                <option value="piece_rate">Piece rate</option>
              </select>
            </Field>
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={props.onClose}
            className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-sm font-medium rounded"
          >
            {mutation.isPending ? "Saving…" : "Hire"}
          </button>
        </div>
      </form>
    </div>
  )
}

function TerminateEmployeeModal(props: {
  slug: string
  employee: Employee
  onClose: () => void
  onTerminated: () => void
}) {
  const [terminatedAt, setTerminatedAt] = useState(new Date().toISOString().slice(0, 10))
  const [reason, setReason] = useState("")
  const [error, setError] = useState("")

  const mutation = useMutation({
    mutationFn: () =>
      apiTenant(props.slug, `/employees/${props.employee.id}/terminate`, {
        method: "POST",
        body: JSON.stringify({ terminatedAt, terminationReason: reason.trim() }),
      }),
    onSuccess: props.onTerminated,
    onError: (err) => setError(err instanceof Error ? err.message : "Failed to terminate"),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError("")
    if (!reason.trim()) {
      setError("Please record a reason for termination.")
      return
    }
    mutation.mutate()
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={handleSubmit} className="bg-white rounded-lg w-full max-w-md shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold">Terminate {props.employee.fullName}</div>
          <button type="button" onClick={props.onClose} className="text-slate-400 hover:text-slate-700">
            ×
          </button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Termination date *">
            <input
              type="date"
              value={terminatedAt}
              onChange={(e) => setTerminatedAt(e.target.value)}
              className="form-input"
            />
          </Field>
          <Field label="Reason *">
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="form-input"
              placeholder="Resignation, end of contract, restructuring, etc."
            />
          </Field>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={props.onClose}
            className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white text-sm font-medium rounded"
          >
            {mutation.isPending ? "Saving…" : "Confirm termination"}
          </button>
        </div>
      </form>
    </div>
  )
}

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{props.label}</div>
      {props.children}
    </label>
  )
}

// Append-only — added below the existing TerminateEmployeeModal.

interface EmployeeFull {
  id: string
  employeeNo: string
  workerType: WorkerType
  fullName: string
  nic: string | null
  email: string | null
  phone: string | null
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  epfNo: string | null
  etfNo: string | null
  tin: string | null
  bankName: string | null
  bankAccountName: string | null
  bankAccountNumber: string | null
  bankBranchCode: string | null
  status: EmployeeStatus
}

function EditEmployeeModal(props: {
  slug: string
  employeeId: string
  onClose: () => void
  onSaved: () => void
}) {
  const detail = useQuery({
    queryKey: ["employee", props.slug, props.employeeId],
    queryFn: () => apiTenant<{ employee: EmployeeFull }>(props.slug, `/employees/${props.employeeId}`),
  })

  const [form, setForm] = useState<Partial<EmployeeFull> | null>(null)
  const [error, setError] = useState("")

  // Hydrate the form once detail arrives.
  if (detail.data && form === null) {
    setForm({ ...detail.data.employee })
  }

  const mutation = useMutation({
    mutationFn: () => {
      // Only send fields that diverge from the initial response — keeps
      // PATCH small and avoids overwriting columns we don't surface here.
      const initial = detail.data!.employee
      const body: Record<string, unknown> = {}
      const fields: (keyof EmployeeFull)[] = [
        "fullName", "nic", "email", "phone",
        "addressLine1", "addressLine2", "city",
        "epfNo", "etfNo", "tin",
        "bankName", "bankAccountName", "bankAccountNumber", "bankBranchCode",
      ]
      for (const f of fields) {
        const v = (form as EmployeeFull)[f] ?? null
        const original = initial[f] ?? null
        if (v !== original) body[f] = v === "" ? null : v
      }
      return apiTenant(props.slug, `/employees/${props.employeeId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      })
    },
    onSuccess: props.onSaved,
    onError: (err) => {
      if (err instanceof ApiError) {
        const details = (err.body as { details?: Array<{ path?: string[]; message: string }> } | null)?.details
        if (Array.isArray(details) && details[0]) {
          setError(`${details[0].path?.join(".")} — ${details[0].message}`)
        } else {
          setError(err.message)
        }
      } else {
        setError(err instanceof Error ? err.message : "Failed to save")
      }
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError("")
    if (!form?.fullName?.trim()) {
      setError("Full name is required.")
      return
    }
    mutation.mutate()
  }

  function fld<K extends keyof EmployeeFull>(key: K, value: EmployeeFull[K]) {
    setForm({ ...form, [key]: value })
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={handleSubmit} className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="font-semibold">Edit employee</div>
            {detail.data && <div className="text-xs text-slate-500 font-mono">{detail.data.employee.employeeNo}</div>}
          </div>
          <button type="button" onClick={props.onClose} className="text-slate-400 hover:text-slate-700">×</button>
        </div>

        {detail.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
        {detail.error && <div className="p-6 text-sm text-red-600">{(detail.error as Error).message}</div>}

        {form && (
          <>
            <div className="p-5 space-y-3">
              <Field label="Full name *">
                <input value={form.fullName ?? ""} onChange={(e) => fld("fullName", e.target.value)} className="form-input" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email">
                  <input type="email" value={form.email ?? ""} onChange={(e) => fld("email", e.target.value)} className="form-input" />
                </Field>
                <Field label="Phone">
                  <input value={form.phone ?? ""} onChange={(e) => fld("phone", e.target.value)} className="form-input" />
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="NIC">
                  <input value={form.nic ?? ""} onChange={(e) => fld("nic", e.target.value)} className="form-input" />
                </Field>
                <Field label="EPF #">
                  <input value={form.epfNo ?? ""} onChange={(e) => fld("epfNo", e.target.value)} className="form-input" />
                </Field>
                <Field label="ETF #">
                  <input value={form.etfNo ?? ""} onChange={(e) => fld("etfNo", e.target.value)} className="form-input" />
                </Field>
              </div>
              <Field label="TIN">
                <input value={form.tin ?? ""} onChange={(e) => fld("tin", e.target.value)} className="form-input" />
              </Field>

              <div className="text-xs font-medium text-slate-500 mt-4 mb-1">Address</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Line 1">
                  <input value={form.addressLine1 ?? ""} onChange={(e) => fld("addressLine1", e.target.value)} className="form-input" />
                </Field>
                <Field label="Line 2">
                  <input value={form.addressLine2 ?? ""} onChange={(e) => fld("addressLine2", e.target.value)} className="form-input" />
                </Field>
              </div>
              <Field label="City">
                <input value={form.city ?? ""} onChange={(e) => fld("city", e.target.value)} className="form-input" />
              </Field>

              <div className="text-xs font-medium text-slate-500 mt-4 mb-1">Bank details (used by salary disbursement file)</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Bank name">
                  <input value={form.bankName ?? ""} onChange={(e) => fld("bankName", e.target.value)} className="form-input" placeholder="Commercial Bank" />
                </Field>
                <Field label="Account name">
                  <input value={form.bankAccountName ?? ""} onChange={(e) => fld("bankAccountName", e.target.value)} className="form-input" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Account number">
                  <input value={form.bankAccountNumber ?? ""} onChange={(e) => fld("bankAccountNumber", e.target.value)} className="form-input" />
                </Field>
                <Field label="Branch code">
                  <input value={form.bankBranchCode ?? ""} onChange={(e) => fld("bankBranchCode", e.target.value)} className="form-input" />
                </Field>
              </div>

              {error && <div className="text-sm text-red-600">{error}</div>}

              <div className="text-xs font-medium text-slate-500 mt-4 mb-1">Pay components</div>
              <PayComponentsSection slug={props.slug} employeeId={props.employeeId} />
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button type="button" onClick={props.onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900">
                Cancel
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-sm font-medium rounded"
              >
                {mutation.isPending ? "Saving…" : "Save changes"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Pay components inside the Edit modal
// ────────────────────────────────────────────────────────────

interface PayComponent {
  id: string
  employeeId: string
  componentCode: string
  componentType: "allowance" | "deduction" | "reimbursement"
  amount: string
  taxable: boolean
  epfEligible: boolean
  effectiveFrom: string
  effectiveTo: string | null
}

interface AllowanceTemplate {
  id: string
  code: string
  name: string
  category: "allowance" | "deduction" | "reimbursement"
  defaultAmount: string | null
  taxable: boolean
  epfEligible: boolean
}

function PayComponentsSection(props: { slug: string; employeeId: string }) {
  const qc = useQueryClient()
  const [showClosed, setShowClosed] = useState(false)
  const list = useQuery({
    queryKey: ["pay-components", props.slug, props.employeeId, showClosed],
    queryFn: () =>
      apiTenant<{ payComponents: PayComponent[] }>(
        props.slug,
        `/pay-components?employeeId=${props.employeeId}${showClosed ? "" : "&active=true"}`,
      ),
  })
  const templates = useQuery({
    queryKey: ["allowance-templates", props.slug],
    queryFn: () => apiTenant<{ allowanceTemplates: AllowanceTemplate[] }>(props.slug, "/settings/allowance-templates"),
  })

  const [showAdd, setShowAdd] = useState(false)
  const [templateId, setTemplateId] = useState("")
  const [amount, setAmount] = useState("")
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10))
  const [error, setError] = useState("")

  const create = useMutation({
    mutationFn: () => {
      const tpl = templates.data?.allowanceTemplates.find((t) => t.id === templateId)
      if (!tpl) throw new Error("Pick a template")
      const amt = amount ? Number(amount) : (tpl.defaultAmount ? Number(tpl.defaultAmount) : 0)
      return apiTenant(props.slug, "/pay-components", {
        method: "POST",
        body: JSON.stringify({
          employeeId: props.employeeId,
          componentCode: tpl.code,
          componentType: tpl.category,
          amount: amt,
          taxable: tpl.taxable,
          epfEligible: tpl.epfEligible,
          effectiveFrom,
        }),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pay-components", props.slug, props.employeeId] })
      setShowAdd(false)
      setTemplateId("")
      setAmount("")
      setError("")
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Failed"),
  })

  const close = useMutation({
    mutationFn: (id: string) => apiTenant(props.slug, `/pay-components/${id}/close`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pay-components", props.slug, props.employeeId] }),
  })

  const items = list.data?.payComponents ?? []

  return (
    <div className="border border-slate-200 rounded">
      <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center justify-end">
        <label className="text-[11px] text-slate-600 flex items-center gap-1">
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
          Show closed
        </label>
      </div>
      {list.isLoading && <div className="p-3 text-xs text-slate-400">Loading…</div>}
      {items.length === 0 && !list.isLoading && (
        <div className="p-3 text-xs text-slate-400">No {showClosed ? "" : "active "}pay components for this employee.</div>
      )}
      {items.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-1.5 font-medium">Code</th>
              <th className="text-left px-3 py-1.5 font-medium">Type</th>
              <th className="text-right px-3 py-1.5 font-medium">Amount</th>
              <th className="text-left px-3 py-1.5 font-medium">From</th>
              <th className="text-left px-3 py-1.5 font-medium">To</th>
              <th className="text-right px-3 py-1.5 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => {
              const isClosed = !!c.effectiveTo && new Date(c.effectiveTo) < new Date()
              return (
              <tr key={c.id} className={`border-b border-slate-100 last:border-b-0 ${isClosed ? "text-slate-400" : ""}`}>
                <td className="px-3 py-1.5 font-mono">{c.componentCode}</td>
                <td className="px-3 py-1.5">{c.componentType}</td>
                <td className="px-3 py-1.5 text-right font-mono">{Number(c.amount).toLocaleString()}</td>
                <td className="px-3 py-1.5 font-mono">{c.effectiveFrom.slice(0, 10)}</td>
                <td className="px-3 py-1.5 font-mono">{c.effectiveTo ? c.effectiveTo.slice(0, 10) : <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-1.5 text-right">
                  {!isClosed && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Close ${c.componentCode}? It will stop applying from today.`)) close.mutate(c.id)
                      }}
                      className="text-[10px] text-red-600 hover:text-red-800"
                    >
                      End now
                    </button>
                  )}
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>
      )}
      <div className="border-t border-slate-200 bg-slate-50 px-3 py-2">
        {!showAdd ? (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="text-xs text-brand-600 hover:text-brand-800"
            disabled={!templates.data || templates.data.allowanceTemplates.length === 0}
          >
            + Add pay component
            {templates.data && templates.data.allowanceTemplates.length === 0 && (
              <span className="ml-2 text-slate-400">(create a template in Settings first)</span>
            )}
          </button>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="form-input text-xs">
                <option value="">Select template…</option>
                {templates.data?.allowanceTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{t.code} ({t.category})</option>
                ))}
              </select>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Override amount"
                className="form-input text-xs"
              />
              <input
                type="date"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
                className="form-input text-xs"
              />
            </div>
            {error && <div className="text-[10px] text-red-600">{error}</div>}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setShowAdd(false); setError("") }}
                className="text-xs text-slate-600 hover:text-slate-900"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!templateId) {
                    setError("Pick a template.")
                    return
                  }
                  create.mutate()
                }}
                disabled={create.isPending}
                className="px-2 py-1 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-xs font-medium rounded"
              >
                {create.isPending ? "Adding…" : "Add"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
